import type { ActionPermission, ModulePermission } from '@luminovo/api-contracts';

import {
    CALCULATION_PERMISSIONS,
    MANUFACTURING_ADMIN_PERMISSIONS,
    MANUFACTURING_PAID_PERMISSIONS,
    QUOTATIONS_V2_ADMIN_PERMISSIONS,
} from '@/shared/permissions/featureFlags';
import { canAccessFeatureFlagsPage } from '@/shared/permissions/featureFlagsPagePermissions';
import { isAuthorizedForModule } from '@/shared/permissions/isAuthorizedForModule';
import {
    isPageAuthorized,
    PageModulePermissionTest,
    PagePermissionContext,
    PagePermissionPredicate,
} from '@/shared/permissions/PagePermissionContext';

export const canViewInternalOrganizationSettings = ({ permissions, userType }: PagePermissionContext): boolean =>
    permissions.includes('view:organization_settings') && userType === 'Internal';

export const isInternalUser: PagePermissionPredicate = ({ userType }) => userType === 'Internal';

/**
 * Who may use the new navigation, the route tree under `/next`: Internal users with `new_navigation`, on for their
 * tenant or for them alone. Customers and suppliers get their tenant's flags too, so the user type is part of it. The
 * browser's choice turns it on (`app/newNavigation.ts`).
 */
export const canUseNewNavigation: PagePermissionPredicate = ({ userType, isFeatureFlagEnabled }) =>
    userType === 'Internal' && isFeatureFlagEnabled('new_navigation');

/** The component and demand importers write the EMS's own data: Internal users who may open the part library. */
export const canOpenPartLibraryImporters: PagePermissionPredicate = ({ userType, permissions }) =>
    userType === 'Internal' && permissions.includes('view:part_library');

/** Internal users, behind `component_importer_v2` (Retool Tenants app) and always on outside production. */
export const canImportComponents: PagePermissionPredicate = ({
    userType,
    isFeatureFlagEnabled,
    isProductionEnvironment,
}) => userType === 'Internal' && (isFeatureFlagEnabled('component_importer_v2') || !isProductionEnvironment);

export const canViewSupplierStrategy: PagePermissionPredicate = ({ permissions, isFeatureFlagEnabled }) =>
    permissions.includes('view:supplier') && isFeatureFlagEnabled('scope_approved_vendor_list');

/**
 * Orders are on the order-portal hosts. Supplier users never see them: the orders backend would treat them as EMS
 * staff.
 */
export const canViewOrders: PagePermissionPredicate = ({ userType, isOrderPortalEnabled }) =>
    isOrderPortalEnabled && (userType === 'Internal' || userType === 'Customer');

/** Uploading orders is for Internal users, on the order-portal hosts. */
export const canUploadOrders: PagePermissionPredicate = ({ userType, isOrderPortalEnabled }) =>
    isOrderPortalEnabled && userType === 'Internal';

/** What the Purchasing pages require without advanced role management. */
export const canViewPurchaseOrders: PagePermissionPredicate = ({ permissions, isPurchasingEnabled }) =>
    isPurchasingEnabled && permissions.includes('view:purchase_order');

/** What the Purchasing page requires with advanced role management. */
export const canViewPurchaseOrdersModule: PageModulePermissionTest = ({ modulePermissions, isPurchasingEnabled }) =>
    isPurchasingEnabled && isAuthorizedForModule(modulePermissions, sourcingViewModulePermissions);

/** Customers land on `/start` from `/`; Internal users preview it from the landing page settings. */
export const canViewCustomerLandingPage: PagePermissionPredicate = ({ userType }) =>
    userType === 'Customer' || userType === 'Internal';

export const canAccessSupplierPortal: PagePermissionPredicate = ({ userType }) =>
    userType === 'Supplier' || userType === 'Internal';

/**
 * The `/customer-portal` tree is for Customer users only, so every page below it may assume a customer. Internal
 * users (the EMS's own staff) preview customer pages at their shared URLs instead (MAN-2864).
 */
export const canAccessCustomerPortal: PagePermissionPredicate = ({ userType }) => userType === 'Customer';

/**
 * Who may create a project: those who hold a create permission and, with advanced role management, the action of the
 * endpoint the New project buttons call. The action passes everyone without advanced role management, so an EMS
 * viewer, who holds `create:rfq:ems` but not the action, is the one the action keeps out.
 */
export const canCreateProject: PagePermissionPredicate = ({ permissions, hasPermissionForAction }) =>
    (permissions.includes('create:rfq:customer') || permissions.includes('create:rfq:ems')) &&
    hasPermissionForAction('CanCreateRfq');

export const canViewUserManagementUsers: PagePermissionPredicate = ({
    permissions,
    userType,
    hasPermissionForAction,
}) =>
    permissions.includes('view:organization_settings') &&
    userType === 'Internal' &&
    hasPermissionForAction('CanAccessUserManagementSettingsPage');

/**
 * The pages of a section of the settings menu, for Internal users, and with advanced role management for those who
 * hold the section's action. The menu shows each entry where its page opens. The `settings/_menu` layout
 * checks `view:organization_settings` above them all.
 */
const settingsSection =
    (action: ActionPermission) =>
    ({ userType, hasPermissionForAction }: Pick<PagePermissionContext, 'userType' | 'hasPermissionForAction'>) =>
        userType === 'Internal' && hasPermissionForAction(action);

export const canViewGeneralSettings = settingsSection('CanAccessGeneralSettingsPage');
export const canViewSitesSettings = settingsSection('CanGetSites');
export const canViewDesignSettings = settingsSection('CanAccessDesignSettingsPage');
export const canViewSourcingSettings = settingsSection('CanAccessSourcingSettingsPage');
export const canViewOtherOrganizationSettings = settingsSection('CanAccessOtherOrganizationSettingsPage');

/** The customer portal's pages, on a tenant that has the portal. */
export const canViewCustomerPortalSettings = (
    context: Pick<PagePermissionContext, 'userType' | 'hasPermissionForAction' | 'currentUser'>,
): boolean =>
    settingsSection('CanAccessCustomerPortalSettingsPage')(context) && context.currentUser.hasAccessToCustomerPortal;

/**
 * The supplier integrations page together with its layout's `view:organization_settings`, for the DigiKey callback,
 * which returns there from outside the settings menu.
 */
export const canOpenSupplierIntegrations: PagePermissionPredicate = (context) =>
    canViewInternalOrganizationSettings(context) && canViewSourcingSettings(context);

/** The additional roles and action permissions of advanced role management, a section of the settings menu. */
export const canViewAdvancedUserManagement = (
    context: Pick<PagePermissionContext, 'userType' | 'hasPermissionForAction' | 'isAdvancedRoleManagementEnabled'>,
): boolean =>
    context.isAdvancedRoleManagementEnabled && settingsSection('CanAccessUserManagementSettingsPage')(context);

/** What `useIsAllowedToEdit` checks with advanced role management: whether the user may change the settings. */
export const organizationSettingsEditModulePermissions = {
    module: 'organization_settings',
    permission: 'edit',
} satisfies PageModulePermissionTest;

/**
 * Who may add and change the PCB settings' panel preferences: those who may open the PCB settings and change the
 * organization settings, which the buttons check (`useIsAllowedToEdit`).
 */
export const canEditPanelPreferences: PagePermissionPredicate = (context) =>
    canViewDesignSettings(context) &&
    isPageAuthorized(context, ['edit:organization_settings'], organizationSettingsEditModulePermissions);

/** The SiliconExpert tabs of the part details page, which its tab bar shows only while `silicon_expert` is on. */
export const canViewSiliconExpertPartTabs: PagePermissionPredicate = ({ permissions, isFeatureFlagEnabled }) =>
    permissions.includes('view:part_library') && isFeatureFlagEnabled('silicon_expert');

/** Available while `silicon_expert` is disabled — the opposite gate of `canViewDataIntegrationsSettings`. */
export const canViewCarbonFootprintsSettings = (
    context: Pick<PagePermissionContext, 'userType' | 'hasPermissionForAction' | 'isFeatureFlagEnabled'>,
): boolean => canViewDesignSettings(context) && !context.isFeatureFlagEnabled('silicon_expert');

/** Available once `silicon_expert` is enabled — the opposite gate of `canViewCarbonFootprintsSettings`. */
export const canViewDataIntegrationsSettings = (
    context: Pick<PagePermissionContext, 'userType' | 'hasPermissionForAction' | 'isFeatureFlagEnabled'>,
): boolean => canViewDesignSettings(context) && context.isFeatureFlagEnabled('silicon_expert');

export const canViewFeatureFlagsSettings: PagePermissionPredicate = (ctx) =>
    ctx.permissions.includes('view:organization_settings') && canAccessFeatureFlagsPage(ctx);

/** The settings of the company a Customer user belongs to. Internal users have no company. */
export const canViewCompanySettings: PagePermissionPredicate = ({ permissions, userType }) =>
    permissions.includes('view:organization_settings') && userType === 'Customer';

export const canViewExportTemplates: PagePermissionPredicate = ({ permissions, userType }) =>
    userType === 'Internal' && permissions.includes('view:organization_settings');

export const canViewQuotationTemplates: PagePermissionPredicate = ({
    permissions,
    userType,
    productCostingAccess,
    isFeatureFlagEnabled,
}) =>
    userType === 'Internal' &&
    isFeatureFlagEnabled('quotation_builder') &&
    productCostingAccess !== 'Lite' &&
    permissions.includes('view:organization_settings');

export const canEditQuotationTemplates: PagePermissionPredicate = ({
    permissions,
    productCostingAccess,
    isFeatureFlagEnabled,
}) =>
    isFeatureFlagEnabled('quotation_builder') &&
    productCostingAccess !== 'Lite' &&
    permissions.includes('edit:organization_settings');

export const canViewManufacturing: PagePermissionPredicate = ({ permissions, productCostingAccess }) =>
    productCostingAccess === 'Full' && MANUFACTURING_PAID_PERMISSIONS.every((p) => permissions.includes(p));

const canManageManufacturing: PagePermissionPredicate = ({ permissions, productCostingAccess }) =>
    productCostingAccess === 'Full' && MANUFACTURING_ADMIN_PERMISSIONS.every((p) => permissions.includes(p));

/**
 * Who may create an activity, expense, resource or driver, which the Add and Duplicate buttons check: admins, and with
 * advanced role management those who may see manufacturing and hold the action of their `POST` endpoints.
 */
export const canCreateManufacturingTemplates: PagePermissionPredicate = (context) =>
    isPageAuthorized(
        context,
        canManageManufacturing,
        (ctx) => canViewManufacturing(ctx) && ctx.hasPermissionForAction('CanCreateManufacturingTemplates'),
    );

/**
 * Who may change an activity, expense, resource or driver, which its row menu and formula errors' Resolve link check:
 * as `canCreateManufacturingTemplates`.
 */
export const canEditManufacturingTemplates: PagePermissionPredicate = (context) =>
    isPageAuthorized(
        context,
        canManageManufacturing,
        (ctx) => canViewManufacturing(ctx) && ctx.hasPermissionForAction('CanUpdateManufacturingTemplates'),
    );

const manufacturingViewModulePermissions = {
    module: 'manufacturing',
    permission: 'view',
} satisfies PageModulePermissionTest;

/**
 * Who may open a project's manufacturing: on the Full plan, those who may view manufacturing, which advanced role
 * management decides by its module.
 */
export const canViewProjectManufacturing: PagePermissionPredicate = (context) =>
    context.productCostingAccess === 'Full' &&
    isPageAuthorized(context, MANUFACTURING_PAID_PERMISSIONS, manufacturingViewModulePermissions);

/**
 * Who may open a project's quotation: those who hold `view:quotation`, and with advanced role management the action
 * of the quotation tab.
 */
export const canViewQuotation: PagePermissionPredicate = ({ permissions, hasPermissionForAction }) =>
    permissions.includes('view:quotation') && hasPermissionForAction('CanAccessQuotationTab');

/** The action check passes everyone without advanced role management, so `view:rfq` keeps out non-viewers. */
export const canEditProject: PagePermissionPredicate = ({ permissions, hasPermissionForAction }) =>
    permissions.includes('view:rfq') && hasPermissionForAction('CanEditRfq');

/** What the calculation pages require without advanced role management. */
export const canViewCalculation: PagePermissionPredicate = ({ permissions, productCostingAccess }) =>
    productCostingAccess !== 'Lite' && CALCULATION_PERMISSIONS.every((p) => permissions.includes(p));

/** What the calculation pages require with advanced role management. */
export const calculationViewModulePermissions = {
    module: 'calculation',
    permission: 'view',
} satisfies PageModulePermissionTest;

/** What the flexible calculation pages require. */
export const canViewFlexibleCalculation: PagePermissionPredicate = (ctx) =>
    ctx.hasPermissionForAction('CanAccessRfqCalculationTab') && canViewCalculation(ctx);

/** What the flexible calculation templates pages require. */
export const canViewFlexibleCalculationTemplates: PagePermissionPredicate = (ctx) =>
    canViewCalculation(ctx) && ctx.hasPermissionForAction('CanViewCalculationTemplate');

const canManageCalculationTemplates: PagePermissionPredicate = ({ permissions, productCostingAccess }) =>
    productCostingAccess !== 'Lite' && QUOTATIONS_V2_ADMIN_PERMISSIONS.every((p) => permissions.includes(p));

/** What the calculation templates pages require: the calculation, and the action of the templates page. */
export const canViewCalculationTemplates: PagePermissionPredicate = (ctx) =>
    canViewCalculation(ctx) && ctx.hasPermissionForAction('CanAccessCalculationTemplatesPage');

/**
 * Who may create a calculation template, which the Add template button checks: admins, and with advanced role
 * management those who may see the calculation templates and hold the action of `POST /calculations/templates`.
 */
export const canCreateCalculationTemplates: PagePermissionPredicate = (context) =>
    isPageAuthorized(
        context,
        canManageCalculationTemplates,
        (ctx) => canViewCalculationTemplates(ctx) && ctx.hasPermissionForAction('CanCreateCalculationTemplate'),
    );

/** Who may change a calculation template, which its edit button and row link check: as `canCreateCalculationTemplates`. */
export const canEditCalculationTemplates: PagePermissionPredicate = (context) =>
    isPageAuthorized(
        context,
        canManageCalculationTemplates,
        (ctx) => canViewCalculationTemplates(ctx) && ctx.hasPermissionForAction('CanUpdateCalculationTemplate'),
    );

/** What the Insights pages require: Internal users, on a tenant with `monitoring_page` or outside production. */
export const canViewMonitoring: PagePermissionPredicate = ({
    userType,
    isFeatureFlagEnabled,
    isProductionEnvironment,
}) => userType === 'Internal' && (isFeatureFlagEnabled('monitoring_page') || !isProductionEnvironment);

/** What the Insights agent's chat requires. The monitoring layout checks `canViewMonitoring` above it. */
export const canUseInsightsAgent: PagePermissionPredicate = ({ isFeatureFlagEnabled, isProductionEnvironment }) =>
    isFeatureFlagEnabled('monitoring_agent') || !isProductionEnvironment;

/** What the savings report requires. The monitoring layout checks `canViewMonitoring` above it. */
export const canViewSavingsReport: PagePermissionPredicate = ({ isFeatureFlagEnabled, isProductionEnvironment }) =>
    isFeatureFlagEnabled('market_savings_report') || !isProductionEnvironment;

export const assembliesModulePermissions = {
    type: 'any',
    permissions: [
        { module: 'design', permission: 'view' },
        { module: 'customer_portal', permission: 'view' },
    ],
} satisfies PageModulePermissionTest;

/**
 * Who may open the assemblies overview: Internal users, and other users on the hosts with the assembly portal.
 * `isOrderPortalEnabled` is that host check too: `isAssemblyPortalEnabled` is the same function.
 */
export const canViewAssemblies: PagePermissionPredicate = (context) =>
    (context.isOrderPortalEnabled || context.userType === 'Internal') &&
    isPageAuthorized(context, ['view:assembly'], assembliesModulePermissions);

export const assembliesEditModulePermissions = {
    type: 'any',
    permissions: [
        { module: 'design', permission: 'edit' },
        { module: 'customer_portal', permission: 'edit' },
    ],
} satisfies PageModulePermissionTest;

export const sourcingEditModulePermissions = {
    module: 'sourcing',
    permission: 'edit',
} satisfies PageModulePermissionTest;

export const sourcingViewModulePermissions = {
    module: 'sourcing',
    permission: 'view',
} satisfies PageModulePermissionTest;

export const sourcingManageModulePermissions = {
    module: 'sourcing',
    permission: 'manage',
} satisfies PageModulePermissionTest;

/**
 * Who may add offers by hand or import them, on `/parts/offers/create` and `/parts/offers/csv-importer`. The buttons
 * that lead there check it too, except the quote requests' Import buttons, which show by the quote importer's access.
 */
export const canAddOffers: PagePermissionPredicate = (context) =>
    isPageAuthorized(context, ['view:sourcing'], sourcingEditModulePermissions);

/** Who may create, edit and duplicate sourcing and quote request templates, which their buttons check too. */
export const canManageSourcingTemplates: PagePermissionPredicate = (context) =>
    isPageAuthorized(context, ['edit:organization_settings'], sourcingManageModulePermissions);

/**
 * With advanced role management, whether the user holds `test` of the sourcing module. Without it, the legacy checks
 * decide alone.
 */
const holdsSourcingModule = (
    { isAdvancedRoleManagementEnabled, modulePermissions }: PagePermissionContext,
    test: ModulePermission,
): boolean => !isAdvancedRoleManagementEnabled || isAuthorizedForModule(modulePermissions, test);

/**
 * Who may see the part library's sourcing data, the offers of a part or an IPN and the quote requests: Internal users,
 * and with advanced role management only those who may view sourcing. The tab bars show the links by it, and a part
 * or an IPN opens on its offers for them.
 */
export const canViewPartLibrarySourcing: PagePermissionPredicate = (context) =>
    isInternalUser(context) && holdsSourcingModule(context, sourcingViewModulePermissions);

/**
 * Who may edit and duplicate an offer of the part library: Internal users, and with advanced role management only those
 * who may edit sourcing. The offer drawer's buttons check it too.
 */
export const canEditPartLibraryOffers: PagePermissionPredicate = (context) =>
    isInternalUser(context) && holdsSourcingModule(context, sourcingEditModulePermissions);

/**
 * Who may see a custom part's offers: those who may open the part library, customers among them, and with advanced
 * role management only those who may view sourcing. Its tab bar shows the tab by it, and the custom part opens on it
 * for them.
 */
export const canViewCustomPartOffers: PagePermissionPredicate = (context) =>
    context.permissions.includes('view:part_library') && holdsSourcingModule(context, sourcingViewModulePermissions);

const canCreateCustomerModule: PageModulePermissionTest = ({ hasPermissionForAction }) =>
    hasPermissionForAction('CanCreateCustomer');

const canAccessCustomersModule: PageModulePermissionTest = ({ hasPermissionForAction }) =>
    hasPermissionForAction('CanAccessCustomersTab');

/** Who may open the customers and a customer's page. */
export const canViewCustomers: PagePermissionPredicate = (context) =>
    isPageAuthorized(context, ['edit:customer'], canAccessCustomersModule);

/** Who may open the customers importer. */
export const canImportCustomers: PagePermissionPredicate = (context) =>
    isPageAuthorized(context, ['edit:customer'], canCreateCustomerModule);

export const canCreateManufacturerModule: PageModulePermissionTest = ({ permissions, hasPermissionForAction }) =>
    permissions.includes('create:manufacturer') && hasPermissionForAction('CanCreateManufacturer');
